// src/components/layout/ScaleWrapper/ScaleWrapper.jsx
//
// The whole app is built as a fixed 375x812 canvas (the iPhone 13 Figma
// mockup size). Every screen's CSS uses absolute px values against that
// canvas, so on any other device the layout either got cropped or left
// gaps at the bottom instead of scaling.
//
// This wrapper renders that fixed canvas once, then applies a single
// uniform scale so it fits the real device viewport proportionately -
// same relative position for every button/gap on any iPhone width, no
// stretching, no per-screen changes needed.
//
// We use CSS `zoom` instead of `transform: scale()` here. Safari/WKWebView
// (which is what this app runs in) has a documented rendering bug where
// `transform: scale()` greater than 1 rasterizes SVG <img>s at their
// unscaled size and then GPU-stretches that bitmap, producing visibly
// blurry/pixelated graphics - worse the larger the scale factor (i.e.
// worse on bigger devices like iPhone Pro Max). `zoom` is a layout
// property rather than a post-paint transform, so Safari lays out and
// rasterizes content at its true final size, avoiding the bug entirely.
import React, { useState, useEffect } from 'react';
import './ScaleWrapper.css';

export const DESIGN_WIDTH = 375;
export const DESIGN_HEIGHT = 812;

const ScaleWrapper = ({ children }) => {
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const updateScale = () => {
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const widthScale = vw / DESIGN_WIDTH;
      const heightScale = vh / DESIGN_HEIGHT;
      // Fit to whichever axis is more constrained so the canvas never
      // overflows the real viewport. For iPhone 13/13 Pro/16/16 Pro/etc.
      // the aspect ratios are nearly identical to the 375x812 mockup, so
      // this lands on essentially the same value either way - no gaps.
      setScale(Math.min(widthScale, heightScale));
    };

    updateScale();
    window.addEventListener('resize', updateScale);
    window.addEventListener('orientationchange', updateScale);
    return () => {
      window.removeEventListener('resize', updateScale);
      window.removeEventListener('orientationchange', updateScale);
    };
  }, []);

  return (
    <div className="scale-wrapper-viewport">
      <div
        className="scale-wrapper-canvas"
        style={{ zoom: scale }}
      >
        {children}
      </div>
    </div>
  );
};

export default ScaleWrapper;
